import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserFollowedDataSubject.translations.json';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from '../../../common/tanstacktable';
import styled from 'styled-components';
import { colorBgContainer, colorGray } from '@atlas-frontend/design-tokens';
import {
  DASHBOARD_GROUP_TABLE_PAGE_SIZE,
  useListUserFollowedDataSubjectQuery,
  UserFollowedDataSubjectParams,
} from './query/useGetUserFollowedDataSubjectQuery';
import { UserFollowedDataSubjectQueryDtoType } from './schema/types';
import { EMPTY_PAGE, useMutationErrors } from '../../../common/queryClient';
import { keepPreviousData } from '@tanstack/react-query';
import {
  CustomPagination,
  ErrorAlertWithDrawer,
  InfoIcon,
  InfoIconUrl,
  Padding,
  Stack,
} from '../../../common/components';
import { Flex, Radio } from 'antd';
import {
  DeleteTableRowButton,
  TableOperationButton,
} from '../../../common/form';
import * as Icon from 'react-feather';
import { UserFollowedDataSubjectModal } from './UserFollowedDataSubjectModal';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutateUserFollowedDataSubjectQuery } from './query/useMutateUserFollowedDataSubjectQuery';
import { useMutateUserDashboardSettingsQuery } from '../userDashboardSettings/query/useMutateUserDashboardSettingsQuery';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { useGetUserDashboardSettingsQuery } from '../userDashboardSettings/query/useGetUserDashboardSettingsQuery';
import { UserDashboardSettingsSchema } from '../userDashboardSettings/schema/UserDashboardSettingsSchema';
import { useDashBoardSettingsPagination } from '../useDashBoardSettingsPagination';
import { useDebounce } from '../../../common/useDebounce';

const { addTranslations, t } = i18nNsFactory('user-followed-data-subject');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

const columnHelper = createColumnHelper<UserFollowedDataSubjectQueryDtoType>();
function getColumns(
  showModal?: () => void,
  onDelete?: (id: number) => void,
): ColumnDef<UserFollowedDataSubjectQueryDtoType, any>[] {
  const columns: ColumnDef<UserFollowedDataSubjectQueryDtoType, any>[] = [];
  columns.push(
    columnHelper.accessor('dataSubject', {
      header: t(`dataSubject.label`),
    }),
    columnHelper.accessor('operations' as any, {
      header: () => (
        <Flex justify="center" gap="small">
          <TableOperationButton
            id="create-userFollowedDataObjectGroup"
            icon={<Icon.Plus size={22} color={colorGray[7]} />}
            title={t('button.add')}
            onClick={showModal}
          />
        </Flex>
      ),
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => (
        <Flex justify="center" gap="small">
          <DeleteTableRowButton
            id={`delete-table-row-${props.row.index}`}
            confirm={{
              onConfirm: () => onDelete?.(props.row.original.id),
            }}
          />
        </Flex>
      ),
      meta: { width: 80 },
    }) as ColumnDef<UserFollowedDataSubjectQueryDtoType, any>,
  );

  return columns;
}

export const UserFollowedDataSubject = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const { queryParameters, paginationProps, tableProps, setTotal } =
    useDashBoardSettingsPagination();
  const debouncedParams = useDebounce(queryParameters);

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useListUserFollowedDataSubjectQuery(
    debouncedParams as UserFollowedDataSubjectParams,
    {
      placeholderData: keepPreviousData,
    },
  );

  const { data: settings, refetch: refetchSettings } =
    useGetUserDashboardSettingsQuery({
      placeholderData: keepPreviousData,
    });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const { mutateAsync: mutateUserFollowedDataSubject } =
    useMutateUserFollowedDataSubjectQuery();
  const { mutateAsync: mutateUserDashboardSettings } =
    useMutateUserDashboardSettingsQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  const handleSuccess = useCallback(() => {
    handleClose();
    void refetch();
  }, [handleClose, refetch]);

  const handleDelete = useCallback(
    (id: number) => {
      clearMutationErrors();
      return mutateUserFollowedDataSubject({ id: id })
        .then(handleSuccess)
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      clearMutationErrors,
      mutateUserFollowedDataSubject,
      handleSuccess,
      setMutationErrors,
    ],
  );

  const handleShowSubjects = useCallback(
    ({ target: { value } }: RadioChangeEvent) => {
      clearMutationErrors();
      return mutateUserDashboardSettings({
        data: {
          ...UserDashboardSettingsSchema.parse(settings),
          showAllSubjects: value === true,
        },
      })
        .then(() => void refetchSettings())
        .catch((apiErrorResponse) => {
          setMutationErrors(apiErrorResponse.response?.data.errors || []);
        });
    },
    [
      clearMutationErrors,
      mutateUserDashboardSettings,
      settings,
      refetchSettings,
      setMutationErrors,
    ],
  );

  const columns: ColumnDef<UserFollowedDataSubjectQueryDtoType, any>[] =
    useMemo(
      () => getColumns(showModal, handleDelete),
      [showModal, handleDelete],
    );

  return (
    <ContentWrapper size="xs">
      <StyledHeader vertical={12} horizontal>
        <Flex justify="space-between">
          <Flex gap="small" align="center">
            <h4>{t('title')}</h4>
            <InfoIcon infoIconUrl={InfoIconUrl.USER_FOLLOWED_DATA_SUBJECT} />
          </Flex>
          <Radio.Group
            optionType="default"
            onChange={handleShowSubjects}
            value={settings?.showAllSubjects}
          >
            <Radio.Button value={true}>{t('showSubjects.all')}</Radio.Button>
            <Radio.Button value={false}>
              {t('showSubjects.followed')}
            </Radio.Button>
          </Radio.Group>
        </Flex>
        {mutationErrors && (
          <ErrorAlertWithDrawer
            errorAction={t('action')}
            validationMessagesMap={mutationErrors}
          />
        )}
      </StyledHeader>
      <Padding horizontal>
        <TableWrapper id="user-followed-data-subject-table-wrapper">
          <Table
            name="user-followed-data-subject-table"
            columns={columns}
            data={content}
            hideClearFilters
            manualFiltering
            manualSorting
            scrollContainerSelector="#user-followed-data-subject-table-wrapper"
            sticky
            virtual
            columnOrderingDisabled={true}
            columnResizeDisabled={true}
            isLoading={isFetching}
            {...tableProps}
          />
        </TableWrapper>
      </Padding>
      <Padding bottom={12} horizontal>
        <FooterPagination
          {...paginationProps}
          showSizeChanger={false}
          showQuickJumper={false}
          pageSize={DASHBOARD_GROUP_TABLE_PAGE_SIZE}
        />
      </Padding>
      {isModalOpen && (
        <UserFollowedDataSubjectModal
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
    </ContentWrapper>
  );
};

const StyledHeader = styled(Padding)`
  border-bottom: 1px solid ${colorGray[2]};
`;

const ContentWrapper = styled(Stack)`
  min-width: 500px;
  background-color: ${colorBgContainer};
  border: 1px solid ${colorGray[2]};
`;

const TableWrapper = styled.div`
  overflow-x: hidden;
  height: 100%;
`;

export const FooterPagination = styled(CustomPagination)`
  float: right;
`;
